import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import * as Yup from "yup";
import * as Const from "../../constants/MandateConstants";
import * as Renders from "../../../../component/AdvancedFiltersRenders";
import { addFilter } from "../../../../utils/FilterUtils";
import { asEndOfDay, asStartOfDay } from "../../../../utils/FinanceDateUtils";
import AdvancedFilter from "../../../../component/AdvancedFilter";
import { STATE_ACTIVE_FILTERS } from "../atoms/MandateAtoms";

function MandateFilters() {
    const { t } = useTranslation("finance", { keyPrefix: "sdd.mandates" });

    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);

    const schema = Yup.object()
            .shape({
                [Const.ID_FILTER_TYPE]: Yup.string(),
                [Const.ID_FILTER_STATUS]: Yup.array(),
                [Const.ID_FILTER_DEBTOR_NAME]: Yup.string().max(
                        128,
                        t("filters.errors.invalid-length")
                ),
                [Const.ID_FILTER_DEBTOR_IBAN]: Yup.string().max(
                        128,
                        t("filters.errors.invalid-length")
                ),
                [Const.ID_FILTER_SUBSCRIBER_FIRST_NAME]: Yup.string().max(
                        128,
                        t("filters.errors.invalid-length")
                ),
                [Const.ID_FILTER_SUBSCRIBER_LAST_NAME]: Yup.string().max(
                        128,
                        t("filters.errors.invalid-length")
                ),
                [Const.ID_FILTER_SUBSCRIBER_TAX_CODE]: Yup.string().max(
                        128,
                        t("filters.errors.invalid-length")
                ),
                [Const.ID_FILTER_SUBSCRIPTION_DATE_FROM]: Yup.date()
                        .nullable()
                        .test(
                                "testCreationFrom",
                                t("filters.errors.invalid-date"),
                                (value, context) => {
                                    return (
                                            !value ||
                                            !context.parent.subscriptionDateTo ||
                                            value <= context.parent.subscriptionDateTo
                                    );
                                }
                        )
                        .typeError(t("filters.errors.invalid-date")),
                [Const.ID_FILTER_SUBSCRIPTION_DATE_TO]: Yup.date()
                        .nullable()
                        .test(
                                "testCreationTo",
                                t("filters.errors.invalid-date"),
                                (value, context) => {
                                    return (
                                            !value ||
                                            !context.parent.subscriptionDateFrom ||
                                            value >= context.parent.subscriptionDateFrom
                                    );
                                }
                        )
                        .typeError(t("filters.errors.invalid-date")),
            })
            .camelCase();

    const defaultValues = {
        type: "",
        status: [],
        debtorName: "",
        debtorIban: "",
        subscriberFirstName: "",
        subscriberLastName: "",
        subscriberTaxCode: "",
        subscriptionDateFrom: null,
        subscriptionDateTo: null
    };

    const fields = {
        main: {
            type: Renders.renderSelect(
                    "type",
                    t("filters." + Const.ID_FILTER_TYPE),
                    Const.TYPES.map((x) => {
                        return { ...x, label: t(x.label) };
                    })
            ),
            status: Renders.renderMultiSelect(
                    "status",
                    t("filters." + Const.ID_FILTER_STATUS),
                    Const.STATUSES.map((x) => {
                        return { ...x, label: t(x.label) };
                    })
            ),
        },
        adv: {
            debtorName: Renders.renderTextField(
                    "debtorName",
                    t("filters." + Const.ID_FILTER_DEBTOR_NAME)
            ),
            debtorIban: Renders.renderTextField(
                    "debtorIban",
                    t("filters." + Const.ID_FILTER_DEBTOR_IBAN)
            ),
            subscriberFirstName: Renders.renderTextField(
                    "subscriberFirstName",
                    t("filters." + Const.ID_FILTER_SUBSCRIBER_FIRST_NAME)
            ),
            subscriberLastName: Renders.renderTextField(
                    "subscriberLastName",
                    t("filters." + Const.ID_FILTER_SUBSCRIBER_LAST_NAME)
            ),
            subscriberTaxCode: Renders.renderTextField(
                    "subscriberTaxCode",
                    t("filters." + Const.ID_FILTER_SUBSCRIBER_TAX_CODE)
            ),
            subscriptionDateFrom: Renders.renderDatePickerFrom(
                    "subscriptionDateFrom",
                    t("filters." + Const.ID_FILTER_SUBSCRIPTION_DATE_FROM)
            ),
            subscriptionDateTo: Renders.rendereDatePickerTo(
                    "subscriptionDateTo",
                    t("filters." + Const.ID_FILTER_SUBSCRIPTION_DATE_TO)
            )
        },
    };

    const submit = (data: any) => {
        addFilter(setFilters, Const.ID_FILTER_TYPE, data.type);
        addFilter(setFilters, Const.ID_FILTER_STATUS, data.status);
        addFilter(setFilters, Const.ID_FILTER_DEBTOR_NAME, data.debtorName);
        addFilter(setFilters, Const.ID_FILTER_DEBTOR_IBAN, data.debtorIban);
        addFilter(setFilters, Const.ID_FILTER_SUBSCRIBER_FIRST_NAME, data.subscriberFirstName);
        addFilter(setFilters, Const.ID_FILTER_SUBSCRIBER_LAST_NAME, data.subscriberLastName);
        addFilter(setFilters, Const.ID_FILTER_SUBSCRIBER_TAX_CODE, data.subscriberTaxCode);
        addFilter(
                setFilters,
                Const.ID_FILTER_SUBSCRIPTION_DATE_FROM,
                asStartOfDay(data.subscriptionDateFrom)
        );
        addFilter(
                setFilters,
                Const.ID_FILTER_SUBSCRIPTION_DATE_TO,
                asEndOfDay(data.subscriptionDateTo)
        );
    };

    const reset = () => {
        setFilters([]);
    };

    return (
            <AdvancedFilter
                    schema={schema}
                    mainFields={fields.main}
                    fields={fields.adv}
                    defaultValues={defaultValues}
                    onSubmit={submit}
                    onReset={reset}
            />
    );
}

export default MandateFilters;